import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Mail, MessageCircle, PhoneCall, FileText, LifeBuoy, Video, Send,
  Sparkles, Inbox as InboxIcon, Reply, FileDown, CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChannelReplyDialog } from "@/components/EmailsTab";
import MbrScheduleDialog from "@/components/Client/MbrScheduleDialog";

/** Iter 167c · Client Timeline — chronological unified feed per client.
 * Renders every touchpoint (email + WA threads, calls, invoices, tickets,
 * meetings, outbound comms) in one scroll. Filter chips per kind.
 */

const KIND_META = {
  email_thread:    { label: "Email",     Icon: Mail,        color: "text-sky-700 border-sky-300 bg-sky-50" },
  whatsapp_thread: { label: "WhatsApp",  Icon: MessageCircle, color: "text-emerald-700 border-emerald-300 bg-emerald-50" },
  inbound_reply:   { label: "Reply",     Icon: InboxIcon,   color: "text-indigo-700 border-indigo-300 bg-indigo-50" },
  comms_sent:      { label: "Comm sent", Icon: Send,        color: "text-purple-700 border-purple-300 bg-purple-50" },
  call:            { label: "Call",      Icon: PhoneCall,   color: "text-amber-700 border-amber-300 bg-amber-50" },
  invoice:         { label: "Invoice",   Icon: FileText,    color: "text-slate-700 border-slate-300 bg-slate-50" },
  ticket:          { label: "Ticket",    Icon: LifeBuoy,    color: "text-rose-700 border-rose-300 bg-rose-50" },
  meeting:         { label: "Meeting",   Icon: Video,       color: "text-cyan-700 border-cyan-300 bg-cyan-50" },
};

export default function ClientTimeline({ wsId, clientId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState(null); // string kind or null = all
  const [replyTid, setReplyTid] = useState(null);
  const [downloadingMbr, setDownloadingMbr] = useState(false);
  const [showMbrSchedule, setShowMbrSchedule] = useState(false);

  const reload = () => {
    if (!wsId || !clientId) return;
    setBusy(true);
    api.get(`/workspaces/${wsId}/clients/${clientId}/timeline`, { params: { limit: 400 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load timeline")))
      .finally(() => setBusy(false));
  };

  useEffect(() => { reload(); }, [wsId, clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const downloadMbr = async () => {
    setDownloadingMbr(true);
    try {
      const res = await api.get(`/workspaces/${wsId}/clients/${clientId}/mbr.pdf`,
                                { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      const name = (data?.client?.name || "client").replace(/\s+/g, "_");
      link.download = `${name}-MBR-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErrorMessage(e, "MBR PDF failed"));
    } finally {
      setDownloadingMbr(false);
    }
  };

  if (busy) return <div className="p-8 text-center text-sm text-muted-foreground">Loading timeline…</div>;
  if (!data) return null;

  const events = filter ? (data.events || []).filter((e) => e.kind === filter) : (data.events || []);
  const totalByKind = data.by_kind || {};

  return (
    <div className="space-y-4" data-testid="client-timeline">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setFilter(null)}
          className={`text-[11px] uppercase tracking-widest border px-2 py-1 ${!filter ? "bg-foreground text-background border-foreground" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="timeline-filter-all"
        >
          All ({data.count})
        </button>
        {Object.entries(KIND_META).map(([k, meta]) => {
          const n = totalByKind[k] || 0;
          if (n === 0) return null;
          const Icon = meta.Icon;
          const active = filter === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(active ? null : k)}
              className={`text-[11px] uppercase tracking-widest border px-2 py-1 inline-flex items-center gap-1 ${active ? "bg-foreground text-background border-foreground" : meta.color}`}
              data-testid={`timeline-filter-${k}`}
            >
              <Icon className="w-3 h-3" /> {meta.label} ({n})
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs rounded-none"
                  onClick={() => setShowMbrSchedule(true)}
                  data-testid="timeline-mbr-schedule-btn">
            <CalendarClock className="w-3 h-3 mr-1" /> Auto-send
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs rounded-none"
                  onClick={downloadMbr} disabled={downloadingMbr}
                  data-testid="timeline-download-mbr">
            <FileDown className="w-3 h-3 mr-1" />
            {downloadingMbr ? "Preparing…" : "Download MBR PDF"}
          </Button>
        </div>
      </div>

      {events.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nothing here yet. Once you email, invoice, or WhatsApp this client, it'll appear on the timeline.
        </div>
      )}

      <ol className="relative border-l-2 border-border pl-6 space-y-4">
        {events.map((e, idx) => {
          const meta = KIND_META[e.kind] || {
            label: e.kind, Icon: Mail, color: "text-muted-foreground border-border bg-background",
          };
          const Icon = meta.Icon;
          const when = (e.at || "").slice(0, 16).replace("T", " ");
          const autoLinked = e.meta?.auto_linked;
          return (
            <li key={`${e.kind}-${idx}`} className="relative" data-testid={`timeline-event-${e.kind}-${idx}`}>
              <span className={`absolute -left-[34px] top-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${meta.color}`}>
                <Icon className="w-3 h-3" />
              </span>
              <div className="border border-border bg-background hover:bg-muted/30 transition-colors p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${meta.color}`}>
                    {meta.label}
                  </span>
                  {e.channel && e.channel !== e.kind && (
                    <span className="text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5 text-muted-foreground">
                      {e.channel}
                    </span>
                  )}
                  {autoLinked && (
                    <span className="text-[10px] uppercase tracking-widest border border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800 px-1.5 py-0.5 inline-flex items-center gap-0.5"
                          title="Auto-linked from sender identity">
                      <Sparkles className="w-2.5 h-2.5" /> Auto
                    </span>
                  )}
                  <span className="text-sm font-medium truncate flex-1">
                    {e.link ? (
                      <Link to={e.link} className="hover:underline">{e.title}</Link>
                    ) : e.title}
                  </span>
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">{when}</span>
                  {(e.kind === "email_thread" || e.kind === "whatsapp_thread") && e.meta?.thread_id && e.channel !== "gmail" && (
                    <button
                      type="button"
                      onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setReplyTid(e.meta.thread_id); }}
                      className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
                      data-testid={`timeline-reply-${idx}`}
                      title="Reply from this page — never leave the client"
                    >
                      <Reply className="w-3 h-3" /> Reply
                    </button>
                  )}
                </div>
                {e.subtitle && (
                  <div className="text-xs text-muted-foreground mt-1 truncate">{e.subtitle}</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {replyTid && (
        <ChannelReplyDialog
          wsId={wsId}
          tid={replyTid}
          onClose={() => setReplyTid(null)}
          onSent={() => { setReplyTid(null); reload(); }}
        />
      )}
      {showMbrSchedule && (
        <MbrScheduleDialog
          wsId={wsId}
          clientId={clientId}
          clientName={data?.client?.name}
          onClose={() => setShowMbrSchedule(false)}
        />
      )}
    </div>
  );
}
